import React from "react";
import styled from "styled-components";
import VerizonLogo from "../pngs/Verizon_2024.svg.png";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import InfoCard from "../components/sub-components/InfoCard";
import MetricsCard from "../components/sub-components/MetricsCard";
import TestimonialSection from "../components/TestimonialsSection";
import NDADisclosure from "../components/NDADisclosure";
import { ContrastPair } from "../components/diagrams/primitives";
import {
    SystemLadder,
    SystemLoop,
    CommerceState,
    PromotionFlow,
    NavigationDomains,
    QALoop,
    OwnedDomains,
} from "../components/VzDiagrams";

/**
 * Verizon.
 *
 * The role where the unit of work stopped being a screen. The page is built around
 * that shift: drift as an engineering cost, the layered system that answers it, and
 * the products that proved whether the system actually held.
 */

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Logo = styled.img`
    display: block;
    max-width: 220px;
    height: auto;
    filter: ${({ theme }) => theme.logoFilter};
    margin-bottom: ${spacing[700]};

    ${media.md} {
        max-width: 160px;
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]} ${spacing[700]};
    margin-bottom: ${spacing[600]};
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: ${spacing[900]};
    align-items: start;

    ${media.lg} {
        grid-template-columns: 1fr;
        gap: ${spacing[700]};
    }
`;

const Sidebar = styled.aside`
    position: sticky;
    top: calc(var(--chrome-h, 145px) + ${spacing[600]});
    align-self: start;

    ${media.lg} {
        position: static;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 68ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Bullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 ${spacing[600]};
    max-width: 68ch;
    columns: 2;
    column-gap: ${spacing[700]};

    ${media.md} {
        columns: 1;
    }
`;

const Bullet = styled.li`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    padding-left: ${spacing[500]};
    position: relative;
    margin-bottom: ${spacing[200]};
    break-inside: avoid;

    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.primary};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const Shipped = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[500]};
    margin: ${spacing[700]} 0;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const ShippedItem = styled.div`
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-left: 2px solid ${({ color }) => color};
    padding: ${spacing[500]};
    min-width: 0;
`;

const ShippedName = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[300]};
`;

const ShippedDesc = styled.p`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0;
`;

/**
 * The closing beat is four words, and they land harder as a stack than as a
 * sentence — so it gets its own block rather than being folded into a Pull.
 */
const UnitStack = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]};
    margin: ${spacing[700]} 0;
`;

const Unit = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[400]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    border: 1px solid ${({ color }) => color};
    padding: ${spacing[400]} ${spacing[500]};
    flex: 1 1 auto;
    text-align: center;
    white-space: nowrap;
`;

const SHIPPED = [
    {
        name: "Verizon Design System",
        color: colors.primary[500],
        desc: "Reusable components, interaction patterns, accessibility standards, and product conventions used across Verizon experiences.",
    },
    {
        name: "Promotions & Upgrades",
        color: colors.amber[400],
        desc: "Product patterns for promotional and upgrade experiences wired into Verizon's customer and commerce workflows.",
    },
    {
        name: "Line & Accessory Addition",
        color: colors.cyan[300],
        desc: "Structured purchasing workflows spanning existing accounts, new lines, devices, plans, and accessories.",
    },
    {
        name: "Mobile Navigation",
        color: colors.emerald[500],
        desc: "Application architecture and navigation across account, billing, shopping, and support experiences.",
    },
    {
        name: "Chat & Support",
        color: colors.purple[400],
        desc: "Support interactions reworked so help stayed discoverable and navigable as the product grew.",
    },
    {
        name: "Billing",
        color: colors.coral[400],
        desc: "Complex billing information and account management reduced to workflows a customer could actually follow.",
    },
];

const UNITS = [
    { name: "Components", color: colors.primary[500] },
    { name: "Patterns", color: colors.cyan[300] },
    { name: "Contracts", color: colors.amber[400] },
    { name: "Systems", color: colors.emerald[500] },
];

const Verizon = () => (
    <PageWrapper>
        <Header>
            <Logo src={VerizonLogo} alt="Verizon logo" />
            <PageHeadline>Verizon</PageHeadline>
            <Meta>
                <MetaItem>
                    <TerminalLabel>Role</TerminalLabel>
                    <span>Software Engineer &mdash; Design Systems</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Service Period</TerminalLabel>
                    <span>Jun 2018 &mdash; Jun 2021</span>
                </MetaItem>
            </Meta>
            <Lede>
                Verizon&rsquo;s mobile products carried millions of customers through wireless
                accounts, device purchases, plan changes, billing, line additions, and support. At
                that scale, building screens is not the job. I worked across the Verizon Design
                System and the customer-facing application at the same time &mdash; defining
                reusable components and interaction patterns, then applying them to production
                commerce, account, billing, navigation, and support workflows. The question was{" "}
                <strong>
                    how to build a product experience once, make it reusable, and keep it consistent
                    across an organisation operating at that size
                </strong>
                .
            </Lede>
        </Header>

        <Layout>
            <Sidebar>
                <InfoCard
                    dates="Jun 2018 – Jun 2021"
                    roles={["Software Engineer", "Design Systems"]}
                    tools={["Design Systems", "Component Architecture", "Accessibility", "Commerce"]}
                />
            </Sidebar>

            <Body>
                <Section>
                    <SectionHeadline>The Engineering Problem</SectionHeadline>
                    <Para>
                        Large applications drift. One team builds a button one way. Another builds a
                        slightly different version. A third solves the same interaction with an
                        entirely different pattern. Multiply that across hundreds of screens,
                        multiple product teams, and millions of customers, and small inconsistencies
                        stop being a polish problem and become an engineering cost.
                    </Para>
                    <Para>
                        Verizon needed a shared system that could support genuinely different product
                        workflows without every team reinventing common interface behaviour. That
                        meant thinking past individual screens, in layers:
                    </Para>
                    <DiagramSlot>
                        <SystemLadder />
                    </DiagramSlot>
                    <Pull>The design system became infrastructure for building the product.</Pull>
                </Section>

                <Section>
                    <SectionHeadline>Building at the Component Layer</SectionHeadline>
                    <Para>
                        I contributed to the reusable component and pattern layer behind Verizon
                        experiences. The work was not defining what something looked like &mdash; it
                        was defining how it behaved once real situations reached it:
                    </Para>
                    <Bullets>
                        <Bullet>Component hierarchy</Bullet>
                        <Bullet>Variants</Bullet>
                        <Bullet>Interaction states</Bullet>
                        <Bullet>Content constraints</Bullet>
                        <Bullet>Responsive behaviour</Bullet>
                        <Bullet>Accessibility</Bullet>
                        <Bullet>Composition</Bullet>
                        <Bullet>Consistency across workflows</Bullet>
                    </Bullets>
                    <Para>
                        A component could not be built for one ideal screenshot. It had to survive
                        different content, different account states, different devices, different
                        accessibility needs, and different business rules &mdash; which changes the
                        question being asked of it entirely.
                    </Para>
                    <ContrastPair
                        left={{
                            label: "Screen thinking",
                            color: colors.coral[400],
                            text: "Does this screen look right?",
                        }}
                        right={{
                            label: "System thinking",
                            color: colors.emerald[500],
                            text: "Can this pattern reliably support the product?",
                        }}
                    />
                </Section>

                <Section>
                    <SectionHeadline>From Components to Product</SectionHeadline>
                    <Para>
                        The other half of the role was using those systems to build the actual
                        customer experiences inside Verizon&rsquo;s mobile ecosystem &mdash; which
                        matters, because a design system cannot be developed in isolation. Real
                        products are what expose its weaknesses.
                    </Para>
                    <DiagramSlot>
                        <SystemLoop />
                    </DiagramSlot>
                    <Para>
                        Working on both sides of that boundary meant the system and the application
                        were not separate projects. Product work is what evolved the system.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Engineering Commerce Workflows</SectionHeadline>
                    <Para>
                        The hardest work was promotional, upgrade, line-addition, and
                        accessory-purchase experiences. Telecom commerce carries considerably more
                        state than a conventional checkout &mdash; what a customer can buy at all
                        depends on a chain of conditions:
                    </Para>
                    <DiagramSlot>
                        <CommerceState />
                    </DiagramSlot>
                    <Para>
                        The interface had to represent changing business conditions without handing
                        that complexity to the customer. I worked on structuring those workflows so
                        account and commerce requirements could be expressed through{" "}
                        <strong>understandable, reusable product patterns</strong> rather than
                        bespoke screens per scenario.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Promotions &amp; Upgrades</SectionHeadline>
                    <Para>
                        Promotions were the sharpest version of that problem, because eligibility and
                        offers shifted with the customer&rsquo;s account and purchase context. The
                        interface had to communicate what they qualified for, what they were buying,
                        what changed on the account, the pricing implications, the promotional
                        conditions, and the next action required.
                    </Para>
                    <DiagramSlot>
                        <PromotionFlow />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Adding Lines &amp; Accessories</SectionHeadline>
                    <Para>
                        Adding a wireless line is a stack of dependent decisions: select a device,
                        configure service, understand pricing, choose accessories, and understand how
                        all of it lands on an account that already exists.
                    </Para>
                    <Para>
                        The engineering challenge was carrying that complexity without exposing the
                        underlying systems to the customer &mdash; structured flows that broke the
                        decisions into predictable steps, reusing existing application patterns
                        wherever the behaviour already existed.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Navigation as Application Architecture</SectionHeadline>
                    <Para>
                        Navigation was never a visual problem. As the application accumulated
                        capabilities, customers needed a predictable way to move between the parts of
                        their account &mdash; and changing navigation meant deciding the information
                        architecture of the whole application.
                    </Para>
                    <DiagramSlot>
                        <NavigationDomains />
                    </DiagramSlot>
                    <Para>
                        Where does functionality belong? What does a customer expect to find under
                        their account? How do features stay discoverable as the product keeps
                        growing? Those answers shaped how the application itself was organised, not
                        just what the menu looked like.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Accessibility at the System Level</SectionHeadline>
                    <Para>
                        At this scale, accessibility cannot be a final review step. Reusable
                        components had to account for it from the start &mdash; readable hierarchy,
                        interaction states, focus behaviour, touch targets, content structure, error
                        communication, contrast, and predictable interaction patterns.
                    </Para>
                    <Pull>
                        Solving accessibility at the system level is leverage. Improve one reusable
                        pattern and every product using it improves.
                    </Pull>
                    <Para>
                        That principle became a permanent part of how I approach design systems.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Building for Change</SectionHeadline>
                    <Para>
                        Enterprise requirements do not stay fixed. Promotions change. Business
                        priorities change. Legal requirements change. Technical constraints appear.
                        Timelines move. I regularly worked across multiple high-priority initiatives
                        where the requirements moved during implementation.
                    </Para>
                    <Para>
                        Reusable systems are what absorbed those changes. Instead of rebuilding an
                        experience every time the requirements shifted, established components and
                        patterns held steady underneath the product logic that was moving.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Production QA</SectionHeadline>
                    <Para>
                        My involvement did not stop at handoff. I worked through implementation and
                        QA to make sure the shipped product behaved the way it was specified to.
                    </Para>
                    <DiagramSlot>
                        <QALoop />
                    </DiagramSlot>
                    <Para>
                        Implementation regularly surfaced conditions that were not obvious during
                        product definition. Working those out directly with engineering is what
                        closed the gap between what was specified and what customers actually
                        received.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>What I Shipped</SectionHeadline>
                    <Shipped>
                        {SHIPPED.map((item) => (
                            <ShippedItem key={item.name} color={item.color}>
                                <ShippedName color={item.color}>{item.name}</ShippedName>
                                <ShippedDesc>{item.desc}</ShippedDesc>
                            </ShippedItem>
                        ))}
                    </Shipped>
                    <DiagramSlot>
                        <OwnedDomains />
                    </DiagramSlot>
                    <MetricsCard
                        metrics={[
                            {
                                value: "Millions",
                                label: "Customers",
                                description: "Nationwide account and commerce experiences",
                                color: colors.emerald[500],
                            },
                            {
                                value: "Shared",
                                label: "Design System",
                                description: "Patterns supporting multiple products and teams, not one application",
                                color: colors.cyan[300],
                            },
                            {
                                value: "System-Level",
                                label: "Impact",
                                description: "One improved component could propagate across many experiences",
                                color: colors.amber[400],
                            },
                        ]}
                    />
                </Section>

                <Section>
                    <SectionHeadline>Retrospective</SectionHeadline>
                    <Para>
                        Verizon changed how I thought about building software at scale. At
                        Caterpillar I learned how to turn complex information and real-world
                        workflows into applications. At Verizon I learned that eventually{" "}
                        <strong>the application itself becomes a system</strong>.
                    </Para>
                    <Para>
                        When millions of customers and multiple engineering teams depend on the same
                        product, the individual screen is no longer the unit of scale.
                    </Para>
                    <UnitStack>
                        {UNITS.map((unit) => (
                            <Unit key={unit.name} color={unit.color}>
                                {unit.name}
                            </Unit>
                        ))}
                    </UnitStack>
                    <Para>
                        That pushed my work deeper into the intersection of product design and
                        engineering. I stopped only asking what experience we should build, and
                        started asking the other question too.
                    </Para>
                    <Pull>
                        What system should we build, so teams can create that experience repeatedly?
                    </Pull>
                </Section>
            </Body>
        </Layout>

        <TestimonialSection
            title="What a Colleague Said"
            testimonials={[
                {
                    name: "Kevin Dixon",
                    title: "Director of UX",
                    body: "During his time supporting my team as a contingent worker, he strove to end up with the best user experience possible, and demonstrated a deep understanding of the technology side of the work. He shared valuable insights with the team regularly, and contributed innovative ideas to the projects he was assigned.",
                },
            ]}
        />

        <NDADisclosure />
    </PageWrapper>
);

export default Verizon;
